import React, { useCallback, useRef } from 'react';
import type { FacilityEntity } from '@/types/facility';
import { ENTITY_RENDER_CONFIG } from '@/types/facility';
import { GRID_SIZE } from './constants';
import { snapToGrid } from '@/utils/gridSnapUtils';
import * as Icons from 'lucide-react';

interface BayObjectProps {
  entity: FacilityEntity;
  isSelected: boolean;
  onSelect: (id: string) => void;
  // `soloMove` is true when the user wants to move just this one entity
  // even if it belongs to a group (Shift held at drag start). The parent
  // editor decides what to do with it — see FacilityEditor.handleMove.
  onMove: (id: string, x: number, y: number, soloMove?: boolean) => void;
  onResize: (id: string, w: number, h: number) => void;
}

export function BayObject({ entity, isSelected, onSelect, onMove, onResize }: BayObjectProps) {
  const config = ENTITY_RENDER_CONFIG[entity.entityType];
  // Capture `solo` at pointer-down so a user pressing Shift mid-drag doesn't
  // flip the move mode under them. PointerEvent doesn't surface modifier
  // state during pointermove on every platform, so we latch it once.
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number; solo: boolean } | null>(null);
  const resizeRef = useRef<{ startX: number; startY: number; origW: number; origH: number } | null>(null);

  // ─── Drag ───────────────────────────────────────────
  const onPointerDown = useCallback((e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onSelect(entity.id);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: entity.x,
      origY: entity.y,
      // Shift OR Alt — Shift is the canonical "edit child" key on most
      // diagramming tools; Alt is a friendlier fallback for users on
      // platforms where Shift collides with other gestures.
      solo: e.shiftKey || e.altKey,
    };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [entity.id, entity.x, entity.y, onSelect]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (dragRef.current) {
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      onMove(
        entity.id,
        snapToGrid(dragRef.current.origX + dx, GRID_SIZE),
        snapToGrid(dragRef.current.origY + dy, GRID_SIZE),
        dragRef.current.solo,
      );
    }
    if (resizeRef.current) {
      const dx = e.clientX - resizeRef.current.startX;
      const dy = e.clientY - resizeRef.current.startY;
      onResize(
        entity.id,
        Math.max(60, snapToGrid(resizeRef.current.origW + dx, GRID_SIZE)),
        Math.max(60, snapToGrid(resizeRef.current.origH + dy, GRID_SIZE)),
      );
    }
  }, [entity.id, onMove, onResize]);

  const onPointerUp = useCallback(() => {
    dragRef.current = null;
    resizeRef.current = null;
  }, []);

  // ─── Resize handle ─────────────────────────────────
  const onResizePointerDown = useCallback((e: React.PointerEvent) => {
    e.stopPropagation();
    resizeRef.current = { startX: e.clientX, startY: e.clientY, origW: entity.width, origH: entity.height };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [entity.width, entity.height]);

  // ─── Icon lookup ────────────────────────────────────
  const IconComponent = (Icons as unknown as Record<string, React.ComponentType<{ className?: string }>>)[config.icon] ?? Icons.Square;

  return (
    <div
      data-bay-object
      className={`absolute rounded-lg shadow-sm border transition-shadow duration-150 select-none
        ${isSelected ? 'ring-2 ring-primary shadow-md scale-[1.01]' : 'hover:shadow-md'}
        ${!entity.isActive ? 'opacity-50' : ''}
        ${entity.isLockedOut ? 'border-destructive/50' : 'border-border'}
      `}
      style={{
        left: entity.x,
        top: entity.y,
        width: entity.width,
        height: entity.height,
        borderLeftWidth: 4,
        borderLeftColor: config.color,
        backgroundColor: 'hsl(var(--card))',
        cursor: 'move',
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    >
      <div className="p-2 h-full flex flex-col justify-between overflow-hidden">
        <div className="flex items-start gap-1.5 min-w-0">
          <IconComponent className="h-4 w-4 shrink-0 mt-0.5 text-muted-foreground" />
          <div className="min-w-0">
            <p className="text-xs font-medium truncate leading-tight">{entity.label}</p>
            <p className="text-[10px] text-muted-foreground truncate">{config.displayLabel}</p>
          </div>
        </div>
        {entity.isLockedOut && (
          <span className="text-[9px] text-destructive truncate">{entity.lockoutReason || 'Locked out'}</span>
        )}
      </div>

      {/* Resize handle */}
      {isSelected && (
        <div
          className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize"
          onPointerDown={onResizePointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
        >
          <svg className="w-full h-full text-muted-foreground/50" viewBox="0 0 16 16">
            <path d="M14 2 L2 14" stroke="currentColor" strokeWidth="1.5" fill="none" />
            <path d="M14 6 L6 14" stroke="currentColor" strokeWidth="1.5" fill="none" />
            <path d="M14 10 L10 14" stroke="currentColor" strokeWidth="1.5" fill="none" />
          </svg>
        </div>
      )}
    </div>
  );
}
